import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { AccordionHeader, AccordionPanel, Field, Input } from '@fluentui/react-components'
import LocalDatePicker from '../../../components/DatePicker/LocalDatePicker'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { useFormikContext } from 'formik'
import { FamilyLawModuleDto } from '../../../hooks/module/useFamilyLaw'
import { css } from '@fluentui/react'
import CountryCombobox from '../../Combobox/i18n/CountryCombobox'
import { FamilyLawAccordionItemProps } from '../../../pages/File/Registration/FamilyLawOverview'
import BaseCombobox from '../../Combobox/BaseCombobox'

export enum RelationshipType {
    Marriage = 'MARRIAGE',
    Registered = 'REGISTERED',
    Cohabitation = 'COHABITATION',
}

export enum RelationshipRecord {
    CommonGoods = 'COMMON',
    Prenup = 'PRENUP',
    NoDeed = 'NO_DEED',
    WithDeed = 'WITH_DEED',
    Agreement = 'AGREEMENT',
    NoAgreement = 'NO_AGREEMENT',
}

export const RelationshipTypeMap: Map<RelationshipType, string> = new Map<RelationshipType, string>([
    [RelationshipType.Marriage, 'Huwelijk'],
    [RelationshipType.Registered, 'Geregistreerd partnerschap'],
    [RelationshipType.Cohabitation, 'Samenwonen'],
])

export const RecordTypeMap: Map<RelationshipRecord, string> = new Map<RelationshipRecord, string>([
    [RelationshipRecord.CommonGoods, 'Gemeenschap van goederen'],
    [RelationshipRecord.Prenup, 'Huwelijkse voorwaarden'],
    [RelationshipRecord.NoDeed, 'Zonder notariële akte'],
    [RelationshipRecord.WithDeed, 'Met notariële akte'],
    [RelationshipRecord.Agreement, 'Onderhandse overeenkomst'],
    [RelationshipRecord.NoAgreement, 'Zonder overeenkomst'],
])

function RelationshipContent(props: FamilyLawAccordionItemProps): ReactElement {
    const { values, initialValues, setFieldValue } = useFormikContext<FamilyLawModuleDto>()
    const themeClassName = useThemeClassName_unstable()
    const [, setType] = useState<string>(RelationshipTypeMap.get(values.relationshipType || initialValues.relationshipType))
    const [, setRecord] = useState<string>(RecordTypeMap.get(values.relationshipRecord || initialValues.relationshipRecord))
    const recordOptions = useMemo(
        () =>
            [
                {
                    key: RelationshipRecord.CommonGoods,
                    text: RecordTypeMap.get(RelationshipRecord.CommonGoods),
                    filter: () => values.relationshipType === RelationshipType.Marriage,
                },
                {
                    key: RelationshipRecord.Prenup,
                    text: RecordTypeMap.get(RelationshipRecord.Prenup),
                    filter: () => values.relationshipType === RelationshipType.Marriage,
                },
                {
                    key: RelationshipRecord.NoDeed,
                    text: RecordTypeMap.get(RelationshipRecord.NoDeed),
                    filter: () => values.relationshipType === RelationshipType.Registered,
                },
                {
                    key: RelationshipRecord.WithDeed,
                    text: RecordTypeMap.get(RelationshipRecord.WithDeed),
                    filter: () => values.relationshipType !== RelationshipType.Marriage,
                },
                {
                    key: RelationshipRecord.Agreement,
                    text: RecordTypeMap.get(RelationshipRecord.Agreement),
                    filter: () => values.relationshipType === RelationshipType.Cohabitation,
                },
                {
                    key: RelationshipRecord.NoAgreement,
                    text: RecordTypeMap.get(RelationshipRecord.NoAgreement),
                    filter: () => values.relationshipType === RelationshipType.Cohabitation,
                },
            ].filter(option => option.filter()),
        [values.relationshipType],
    )
    const changeRecord = (type: RelationshipType) => {
        switch (type) {
            case RelationshipType.Marriage:
                setFieldValue('relationshipRecord', RelationshipRecord.CommonGoods)
                setRecord(RecordTypeMap.get(RelationshipRecord.CommonGoods))
                break
            case RelationshipType.Registered:
                setFieldValue('relationshipRecord', RelationshipRecord.NoDeed)
                setRecord(RecordTypeMap.get(RelationshipRecord.NoDeed))
                break
            case RelationshipType.Cohabitation:
                setFieldValue('relationshipRecord', RelationshipRecord.NoAgreement)
                setFieldValue('relationshipLocation', undefined)
                setFieldValue('relationshipCountry', undefined)
                setRecord(RecordTypeMap.get(RelationshipRecord.NoAgreement))
                break
        }
    }

    return (
        <div className={css(props.styles.container)}>
            <div className={css(props.useColumns ? props.styles.relationBase : undefined)}>
                <Field label="Vorm" required>
                    <BaseCombobox
                        onOptionSelect={(_, data) => {
                            setFieldValue('relationshipType', data.optionValue)
                            setType(data.optionText)
                            changeRecord(data.optionValue as RelationshipType)
                        }}
                        entities={[RelationshipType.Marriage, RelationshipType.Registered, RelationshipType.Cohabitation]}
                        getEntityLabel={entity => RelationshipTypeMap.get(entity)}
                        getEntityValue={entity => entity.toString()}
                        selectedOptions={[values.relationshipType]}
                    />
                </Field>
                <Field label="Wijze van vastlegging" required>
                    <BaseCombobox
                        onOptionSelect={(_, data) => {
                            setRecord(data.optionText)
                            setFieldValue('relationshipRecord', data.optionValue)
                        }}
                        entities={recordOptions}
                        getEntityLabel={entity => entity.text}
                        getEntityValue={entity => entity.key}
                        selectedOptions={[values.relationshipRecord]}
                    />
                </Field>
                <Field label="Datum" required>
                    <LocalDatePicker
                        popupSurface={{ className: themeClassName }}
                        isoValue={values.relationshipDate}
                        onSelectDate={date => setFieldValue('relationshipDate', date)}
                    />
                </Field>
            </div>

            {values.relationshipType !== RelationshipType.Cohabitation && (
                <div className={css(props.useColumns ? props.styles.relationLocation : undefined)}>
                    <Field label="Plaats" required>
                        <Input value={values.relationshipLocation} onChange={(_, data) => setFieldValue('relationshipLocation', data.value)} />
                    </Field>
                    <Field label="Land" required>
                        <CountryCombobox
                            selectedOptions={[values.relationshipCountry]}
                            onOptionSelect={(_, data) => setFieldValue('relationshipCountry', data.selectedOptions?.[0])}
                        />
                    </Field>
                </div>
            )}
        </div>
    )
}

export default function Relationship(props: FamilyLawAccordionItemProps): ReactElement {
    return (
        <div>
            <AccordionHeader>Relatie</AccordionHeader>
            <AccordionPanel>
                <RelationshipContent {...props} />
            </AccordionPanel>
        </div>
    )
}
